export type CookieConsent = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
  consentedAt: string;
  version: number;
};

const COOKIE_CONSENT_STORAGE_KEY = "aai.cookie-consent.v1";
const COOKIE_CONSENT_VERSION = 1;

export function readCookieConsent(): CookieConsent | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<CookieConsent>;
    if (
      parsed.version !== COOKIE_CONSENT_VERSION ||
      typeof parsed.analytics !== "boolean" ||
      typeof parsed.marketing !== "boolean" ||
      typeof parsed.preferences !== "boolean" ||
      typeof parsed.consentedAt !== "string"
    ) {
      return null;
    }
    return {
      necessary: true,
      analytics: parsed.analytics,
      marketing: parsed.marketing,
      preferences: parsed.preferences,
      consentedAt: parsed.consentedAt,
      version: COOKIE_CONSENT_VERSION,
    };
  } catch {
    return null;
  }
}

export function saveCookieConsent(consent: Omit<CookieConsent, "necessary" | "version" | "consentedAt">): CookieConsent {
  const payload: CookieConsent = {
    necessary: true,
    analytics: consent.analytics,
    marketing: consent.marketing,
    preferences: consent.preferences,
    consentedAt: new Date().toISOString(),
    version: COOKIE_CONSENT_VERSION,
  };
  if (typeof window !== "undefined") {
    window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(payload));
    window.dispatchEvent(new CustomEvent("cookie-consent-updated", { detail: payload }));
  }
  return payload;
}

export function hasCookieConsent(category: keyof Pick<CookieConsent, "necessary" | "analytics" | "marketing" | "preferences">): boolean {
  if (category === "necessary") return true;
  const consent = readCookieConsent();
  return Boolean(consent?.[category]);
}
